import { type Edge } from '@xyflow/react'
import { useCallback, useEffect, useState } from 'react'

import { isValidBoundaryEventDrop } from '@flowx/designer/modules/bpmn-editor/boundary-event-validation.util'
import { NodeEntity } from '@flowx/designer/shared/entities/node.entity'

import { BpmnDiagramProps } from './bpmn-diagram.types'
import { SEQUENCE_EDGE_TYPE, SequenceEdgeData, findNodeAt } from './bpmn-graph.mapper'
import { PaletteDropPreviewState } from './components/PaletteDropPreview'
import { snapPointToGrid } from './grid-snapping.util'
import { BOUNDARY_DIMENSIONS, getNodeDimensions } from './node-visuals'
import { planBoundaryDrop, planLaneNodeDrop } from './palette-drop.util'

import { BpmnShapeType, GRID_SNAP_SIZE } from '../../bpmn-graph.constants'
import { Point } from '../../dictionary/geometry.types'

export interface PaletteDndApi {
  /** In-canvas preview of what the drop will produce, or null when idle */
  palettePreview: PaletteDropPreviewState | null
  /** Sequence edge highlighted as a splice target, or null */
  dragOverEdgeId: string | null
  onDragOver: (event: React.DragEvent) => void
  onDragLeave: (event: React.DragEvent) => void
  onDrop: (event: React.DragEvent) => void
}

/**
 * Palette drag & drop onto the canvas: previews the
 * pending node while a drag is over the canvas, then routes the drop to a
 * sequence splice, a boundary event on a node, or a plain lane placement.
 *
 * The placement rules live in palette-drop.util; this owns only the React state
 * the gesture needs, and runs preview and drop through the same rules so the
 * preview never promises a drop that will be rejected.
 */
export function usePaletteDnd({
  data,
  edges,
  readonly,
  paletteDragType,
  screenToFlowPosition,
  onPaletteDrop,
  onNodeDrop,
  onSequenceDrop,
  onDropError,
}: {
  data: BpmnDiagramProps['data']
  /** Live RF edges — a splice target is resolved against what is rendered */
  edges: Edge[]
  readonly: boolean
  paletteDragType: BpmnDiagramProps['paletteDragType']
  screenToFlowPosition: (point: Point) => Point
  onPaletteDrop: BpmnDiagramProps['onPaletteDrop']
  onNodeDrop: BpmnDiagramProps['onNodeDrop']
  onSequenceDrop: BpmnDiagramProps['onSequenceDrop']
  onDropError: BpmnDiagramProps['onDropError']
}): PaletteDndApi {
  // In-canvas palette drag preview: follows the cursor in flow coordinates,
  // tinted by drop validity, snapped on the target node border over
  // boundary-event targets.
  const [palettePreview, setPalettePreview] = useState<PaletteDropPreviewState | null>(null)

  // Sequence edge under a palette drag — highlights it and advertises the
  // drop-to-splice gesture (sequenceDragEnter/dragLeave).
  const [dragOverEdgeId, setDragOverEdgeId] = useState<string | null>(null)

  // Drag ended (dropped or cancelled anywhere): the host clears paletteDragType
  useEffect(() => {
    if (!paletteDragType) {
      setPalettePreview(null)
      setDragOverEdgeId(null)
    }
  }, [paletteDragType])

  // The drag/drop event target is whatever sits under the cursor — React Flow's
  // wide invisible edge-interaction path included, so a plain DOM lookup gives
  // the object under the cursor.
  const edgeAtEventTarget = useCallback(
    (event: React.DragEvent): Edge<SequenceEdgeData> | null => {
      const edgeId = (event.target as Element)
        .closest?.('.react-flow__edge')
        ?.getAttribute('data-id')

      const edge = edgeId ? edges.find((candidate) => candidate.id === edgeId) : null

      return edge?.type === SEQUENCE_EDGE_TYPE ? (edge as Edge<SequenceEdgeData>) : null
    },
    [edges]
  )

  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      if (!paletteDragType || readonly) {
        return
      }
      const flowPoint = screenToFlowPosition({ x: event.clientX, y: event.clientY })
      const targetNode = findNodeAt(flowPoint, data, getNodeDimensions)
      const validBoundaryDrop = isValidBoundaryEventDrop(
        targetNode ? BpmnShapeType.BPMN_NODE : null,
        targetNode?.type ?? null,
        paletteDragType
      )

      // Over a sequence edge with a node-type item: highlight the edge — dropping
      // splices the node into the sequence.
      const targetEdge =
        !targetNode && NodeEntity.isNodeDefinitionType(paletteDragType)
          ? edgeAtEventTarget(event)
          : null
      setDragOverEdgeId(targetEdge?.id ?? null)
      if (targetEdge) {
        setPalettePreview({ position: flowPoint, valid: true, snappedBoundary: false })

        return
      }

      // Valid boundary-event target: preview the boundary event snapped on the
      // node border, exactly where onDrop will place it.
      if (validBoundaryDrop) {
        setPalettePreview({
          position: planBoundaryDrop(flowPoint, targetNode, data, BOUNDARY_DIMENSIONS)
            .absoluteCenter,
          valid: true,
          snappedBoundary: true,
        })

        return
      }

      // Same validity the drop itself will apply, so the preview never lies
      const snappedCenter = snapPointToGrid(flowPoint, GRID_SNAP_SIZE)
      setPalettePreview({
        position: snappedCenter,
        valid: planLaneNodeDrop(snappedCenter, paletteDragType, data, getNodeDimensions).valid,
        snappedBoundary: false,
      })
    },
    [paletteDragType, readonly, screenToFlowPosition, data, edgeAtEventTarget]
  )

  const onDragLeave = useCallback((event: React.DragEvent) => {
    // Only hide when the drag actually leaves the canvas, not when crossing children
    if (!event.currentTarget.contains(event.relatedTarget as Element)) {
      setPalettePreview(null)
      setDragOverEdgeId(null)
    }
  }, [])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      setPalettePreview(null)
      setDragOverEdgeId(null)
      if (readonly) {
        return
      }
      const raw = event.dataTransfer.getData('text/plain')
      if (!raw) {
        return
      }
      let droppedEntity
      try {
        droppedEntity = JSON.parse(raw)
      } catch {
        onDropError('Could not read the dropped item')

        return
      }
      const flowPoint = screenToFlowPosition({ x: event.clientX, y: event.clientY })

      // Drop landed on a sequence edge with a node-type item → splice it into the
      // sequence. The DOM target settles precedence: over a node the target is
      // the node's element, never the edge path.
      const targetEdge = NodeEntity.isNodeDefinitionType(droppedEntity.type)
        ? edgeAtEventTarget(event)
        : null
      if (targetEdge) {
        onSequenceDrop({
          sequence: (targetEdge.data as SequenceEdgeData).sequence,
          droppedEntity,
          position: flowPoint,
        })

        return
      }

      // Drop landed on a node → boundary-event candidate:
      // snap the boundary onto the border closest to the cursor.
      const targetNode = findNodeAt(flowPoint, data, getNodeDimensions)
      if (targetNode) {
        onNodeDrop({
          targetNode,
          position: planBoundaryDrop(flowPoint, targetNode, data, BOUNDARY_DIMENSIONS).relative,
          droppedEntity,
        })

        return
      }

      const plan = planLaneNodeDrop(
        snapPointToGrid(flowPoint, GRID_SNAP_SIZE),
        droppedEntity.type,
        data,
        getNodeDimensions
      )
      if (!plan.valid) {
        onDropError(plan.error)

        return
      }
      onPaletteDrop({
        swimlane: plan.swimlane,
        position: plan.position,
        droppedEntity,
      })
    },
    [
      readonly,
      screenToFlowPosition,
      data,
      onPaletteDrop,
      onNodeDrop,
      onDropError,
      onSequenceDrop,
      edgeAtEventTarget,
    ]
  )

  return { palettePreview, dragOverEdgeId, onDragOver, onDragLeave, onDrop }
}
